/** License document dates: date* are instants; *On are calendar dates. */
const CALENDAR_DATE = /^(\d{4})-?(\d{2})-?(\d{2})$/;
const ISO_INSTANT = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d{1,9})?(?:Z|[+-]\d{2}:\d{2})$/;

function invalidDate(field) {
  const error = new Error(`${field} must be a valid date or null`);
  error.status = 400;
  throw error;
}

function calendarDate(value, field) {
  const match = value.match(CALENDAR_DATE);
  if (!match) return null;
  const [, year, month, day] = match;
  if (Number(year) < 1) invalidDate(field);
  const iso = `${year}-${month}-${day}`;
  const date = new Date(`${iso}T00:00:00.000Z`);
  if (!Number.isFinite(date.getTime()) || date.toISOString().slice(0, 10) !== iso) invalidDate(field);
  return iso;
}

/** Accept SDK timestamps and both JSON timestamp shapes without losing nanoseconds. */
export function licenseTimestampParts(value, field = 'date') {
  if (value == null || value === '') return null;
  let seconds, nanoseconds;
  if (typeof value === 'object' && ('seconds' in value || '_seconds' in value)) {
    seconds = value.seconds ?? value._seconds;
    nanoseconds = value.nanoseconds ?? value._nanoseconds ?? 0;
  } else {
    if (typeof value?.toDate === 'function') value = value.toDate();
    if (typeof value === 'string') {
      value = value.trim();
      if (!value) return null;
      const day = calendarDate(value, field);
      if (day) value = `${day}T00:00:00.000Z`;
      else {
        if (!ISO_INSTANT.test(value)) invalidDate(field);
        calendarDate(value.slice(0, 10), field);
      }
      const millis = Date.parse(value);
      seconds = Math.floor(millis / 1000);
      // Date.parse truncates fractions to milliseconds; preserve the original precision.
      const fraction = value.match(/\.(\d+)(?:Z|[+-])/);
      nanoseconds = fraction ? Number(fraction[1].padEnd(9, '0')) : 0;
    } else {
      const millis = value instanceof Date ? value.getTime() : value;
      if (typeof millis !== 'number' || !Number.isFinite(millis)) invalidDate(field);
      seconds = Math.floor(millis / 1000);
      nanoseconds = Math.round((millis - seconds * 1000) * 1e6);
    }
  }
  if (!Number.isInteger(seconds) || seconds < -62135596800 || seconds > 253402300799
    || !Number.isInteger(nanoseconds) || nanoseconds < 0 || nanoseconds >= 1e9) invalidDate(field);
  return { seconds, nanoseconds };
}

/** Legacy instants use their UTC day; date-only strings never pass through local time. */
export function normalizeLicenseDay(value, field = 'issuedOn') {
  if (typeof value === 'string') {
    const day = calendarDate(value.trim(), field);
    if (day) return day;
  }
  const parts = licenseTimestampParts(value, field);
  return parts ? new Date(parts.seconds * 1000).toISOString().slice(0, 10) : null;
}

/** Normalize only document fields, leaving arbitrary application answers/snapshots alone. */
export function normalizeLicenseDateFields(data, {
  timestampFromParts, isFieldValue = () => false, isDeleteField = () => false
}) {
  const result = { ...data };
  for (const [field, value] of Object.entries(result)) {
    const instant = field.startsWith('date');
    if (!instant && !field.endsWith('On')) continue;
    if (value === undefined) {
      delete result[field];
    } else if (isFieldValue(value)) {
      // Trusted SDK sentinels (server timestamps/deletions) are not JSON values.
      if (!instant && !isDeleteField(value)) invalidDate(field);
      continue;
    } else if (instant) {
      const parts = licenseTimestampParts(value, field);
      result[field] = parts ? timestampFromParts(parts.seconds, parts.nanoseconds) : null;
    } else {
      result[field] = normalizeLicenseDay(value, field);
    }
  }
  return result;
}

export function licenseDatesEqual(left, right, { dateOnly = false } = {}) {
  if (left === right) return true;
  try {
    if (dateOnly) return normalizeLicenseDay(left) === normalizeLicenseDay(right);
    const a = licenseTimestampParts(left), b = licenseTimestampParts(right);
    return a?.seconds === b?.seconds && a?.nanoseconds === b?.nanoseconds;
  } catch {
    return false;
  }
}
